import {
  ISupportingActor,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { BackgroundCheckErrorType } from '@dadohr/shared/domainTypes/instance';
import {
  getNestedKeyPart,
  isCustomFieldKey
} from '@dadohr/shared/domainTypes/org';
import { Link as RouterLink } from 'react-router-dom';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { useRoutes } from '../../../../routes';
import { getParticipantActor } from '../../../../services/experiences/instance';
import { humanReadableActorName } from '../../../../services/experiences/template';
import { LightMdToJsx } from '../../../../services/formatter';
import { standardUserFields } from '../../../../services/user';
import type { SendBackgroundCheckErrorWithData } from './InstanceProblems';
import { findParentTaskTemplate } from './issues/taskTemplateLookup';

/**
 * Human label for the user-profile field the work location was read from.
 *
 * The key comes off the error itself rather than the template, so the row
 * keeps naming the field that actually failed even if an admin re-points the
 * subtask afterwards. Returns null when the error carries no context — errors
 * written before the backend sent it, and every non-work-location cause.
 */
const getWorkLocationFieldLabel = (
  errData: SendBackgroundCheckErrorWithData,
  t: UnsafeTFunction
): string | null => {
  const key = errData.data?.workLocationSourceField;
  if (!key) return null;
  // Custom fields show their bare key rather than the admin's label — looking
  // the label up would mean threading the org's field list through the whole
  // issue-copy layer for this one string. Standard fields (`address`,
  // `location`) are the common case and resolve properly.
  if (isCustomFieldKey(key)) return getNestedKeyPart(key);
  return standardUserFields(t).find((f) => f.key === key)?.label || key;
};

export const getBackgroundCheckDetail = ({
  errData,
  supportingActors,
  t
}: {
  errData: SendBackgroundCheckErrorWithData;
  supportingActors: { [actorKey: string]: ISupportingActor };
  t: UnsafeTFunction;
}): string => {
  switch (errData.errorType) {
    case BackgroundCheckErrorType.MISSING_PERSONAL_EMAIL:
      return t('experienceIssue.backgroundCheck.missingPersonalEmail.detail', {
        actor: humanReadableActorName(PARTICIPANT_ACTOR_KEY, supportingActors),
        addressType: t('experienceIssue.backgroundCheck.personalEmail')
      });
    case BackgroundCheckErrorType.PACKAGE_UNAVAILABLE:
      return t('experienceIssue.backgroundCheck.packageUnavailable.detail');
    // Only the two employee-data causes get their own copy. Everything else,
    // WORK_LOCATION_FIELD_NOT_CONFIGURED included, falls through to the
    // generic Checkr error. Both are terminal — the operation aborts rather
    // than retrying — so the issue stays until someone fixes the profile.
    case BackgroundCheckErrorType.MISSING_WORK_LOCATION: {
      const field = getWorkLocationFieldLabel(errData, t);
      return field
        ? t('Missing working location (**{{field}}**)', { field })
        : t('Missing working location');
    }
    case BackgroundCheckErrorType.UNRECOGNIZED_WORK_LOCATION: {
      // The backend puts the offending value on `data.workLocationValue`,
      // already formatted and truncated for display. It omits the key when
      // the value is empty, so fall back to the bare message.
      const value = errData.data?.workLocationValue;
      return value
        ? // `nsSeparator: false` because the default is ':' — without it
          // i18next splits this key into namespace "Invalid working location"
          // + key '"{{value}}"' and renders only the quoted value.
          t('Invalid working location: "{{value}}"', {
            value,
            nsSeparator: false
          })
        : t('Invalid working location');
    }
    default:
      return t('Unknown Checkr error');
  }
};

export const BackgroundCheckSuggestedAction = ({
  errData
}: {
  errData: SendBackgroundCheckErrorWithData;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();

  // Deep-links to the participant's user profile, degrading to plain text when
  // the participant can't be resolved. Shared by the causes that are fixed on
  // the profile so each of those stays a single `case` about its own copy.
  const renderProfileAction = (label: string) => {
    const participantUserId = getParticipantActor(errData.instance)?.userId;
    if (!participantUserId) {
      return <LightMdToJsx>{label}</LightMdToJsx>;
    }
    return (
      <RouterLink to={ROUTES.users.profile.url(participantUserId)}>
        {label}
      </RouterLink>
    );
  };

  switch (errData.errorType) {
    case BackgroundCheckErrorType.MISSING_PERSONAL_EMAIL: {
      // Deep-link to the participant's user profile, where the personal
      // email field lives. Same missing-data fix path as
      // CANNOT_ASSIGN_SUPPORTING_ACTOR / CANNOT_SEND_CC_MISSING_EMAIL.
      const participantUserId = getParticipantActor(errData.instance)?.userId;
      const label = t(
        'experienceIssue.backgroundCheck.missingPersonalEmail.action'
      );
      if (!participantUserId) {
        return <LightMdToJsx>{label}</LightMdToJsx>;
      }
      return (
        <RouterLink to={ROUTES.users.profile.url(participantUserId)}>
          {label}
        </RouterLink>
      );
    }
    case BackgroundCheckErrorType.PACKAGE_UNAVAILABLE: {
      // Deep-link into the task builder, focused on the task that owns the
      // background-check sub-task. Same edit-route used by
      // GoToTimelineElementEditButton.
      const parentTask = findParentTaskTemplate(errData.tpl, errData.subtaskTplId);
      const label = t(
        'experienceIssue.backgroundCheck.packageUnavailable.action'
      );
      if (!parentTask) {
        return <LightMdToJsx>{label}</LightMdToJsx>;
      }
      return (
        <RouterLink
          to={ROUTES.experiences.details.edit.url(errData.tpl.id, {
            step: 'tasks',
            taskId: parentTask.id
          })}
        >
          {label}
        </RouterLink>
      );
    }
    // The field exists but is empty for this participant. Fixed on their user
    // profile, same deep-link path as MISSING_PERSONAL_EMAIL.
    case BackgroundCheckErrorType.MISSING_WORK_LOCATION:
      return renderProfileAction(
        t('Update this field on their user profile to include a US state')
      );
    // The field holds something that isn't readable as a US state. Also fixed
    // on the profile, but the copy names the field so the admin knows which
    // value to correct.
    case BackgroundCheckErrorType.UNRECOGNIZED_WORK_LOCATION: {
      const field = getWorkLocationFieldLabel(errData, t);
      return renderProfileAction(
        field
          ? t(
              'Update ({{field}}) on their user profile to include a US state',
              { field }
            )
          : t('Update this field on their user profile to include a US state')
      );
    }
    default:
      return (
        <LightMdToJsx>{t('Contact support')}</LightMdToJsx>
      );
  }
};
